import Accordion from '@/common/components/Accordion'
import SelectController from '@/common/components/SelectController'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { RequestedReview } from '@/features/establishmentLeadsFunnel/components/RequestedReview'
import { ReviewButtons } from '@/features/establishmentLeadsFunnel/components/ReviewButtons'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { SpecialFeeItem } from '@/features/publicCompanyLeadsFunnel/components/SpecialFeeItem'
import { usePublicCompanyLead } from '@/features/publicCompanyLeadsFunnel/hooks/usePublicCompanyLead'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { PublicCompanyLeadData, SpecialEntriesData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { UseMutateFunction } from '@tanstack/react-query'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface SpecialFeesProps {
  fee_rule?: PublicCompanyLeadData['fee_rule']
  createPublicCompanyLeadSpecialFee?: UseMutateFunction<
    void,
    Error,
    { value: string; type: string; fee_template_id: string },
    unknown
  >
  createPublicCompanyLeadSpecialFeeIsPending?: boolean
  deletePublicCompanyLeadSpecialFee?: UseMutateFunction<void, Error, string, unknown>
  approveSpecialFeesInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  declineSpecialFeesInformationLead?: UseMutateFunction<void, Error, { description?: string } | undefined, unknown>
  disabled?: boolean
}

export const SpecialFees = ({
  fee_rule,
  createPublicCompanyLeadSpecialFee,
  createPublicCompanyLeadSpecialFeeIsPending,
  deletePublicCompanyLeadSpecialFee,
  approveSpecialFeesInformationLead,
  declineSpecialFeesInformationLead,
  disabled,
}: SpecialFeesProps) => {
  const [specialEntries, setSpecialEntries] = useState(fee_rule?.special_entries || [])

  const { id } = useParams()

  const [searchEstablishment, setSearchEstablishment] = useDebounce('')

  const { control } = useFormContext<PublicCompanyLeadsFunnelUpdateValidationSchema>()

  const { isFetching: publicCompanyIsFetching } = usePublicCompanyLead(id || '', false)

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishment },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer({
        valueFieldName: 'id',
        addAllEstablishmentOption: true,
      }),
    }),
  })

  const deleteSpecialEntry = ({ id, temporary }: { id: string; temporary?: boolean }) => {
    if (temporary) {
      setSpecialEntries(specialEntries.filter((entry) => entry.temporary_target_id !== id))
      return
    }
    deletePublicCompanyLeadSpecialFee?.(id)
  }

  useEffect(() => {
    !publicCompanyIsFetching && setSpecialEntries(fee_rule?.special_entries || [])
  }, [publicCompanyIsFetching])

  return (
    <Accordion
      className="py-1"
      contentClassName="data-[state=open]:overflow-visible"
      name="lead_special_fees"
      head={
        <div className="flex w-full items-center justify-between mr-4">
          <p>Tarifas especiais</p>

          {approveSpecialFeesInformationLead && declineSpecialFeesInformationLead && (
            <div className="ml-auto">
              <ReviewButtons
                reviewState={fee_rule?.last_review?.state}
                onApprove={approveSpecialFeesInformationLead}
                onDecline={declineSpecialFeesInformationLead}
              />
            </div>
          )}
        </div>
      }
      body={
        <div className="flex flex-col gap-4">
          {fee_rule?.last_review?.state === 'disapproved' && (
            <RequestedReview description={fee_rule?.last_review?.description} />
          )}

          <SelectController
            control={control}
            name="fee_rule.special_entries"
            placeholder="Pesquisar Estabelecimentos"
            value={null}
            onInputChange={setSearchEstablishment}
            triggerOnChange={(newValue: { label: string; value: string; type: string }) => {
              setSpecialEntries([
                ...specialEntries,
                {
                  id: newValue.value,
                  temporary_target_id: newValue.value,
                  temporary_target_name: newValue.label,
                  temporary_target_type: newValue.type,
                } as SpecialEntriesData,
              ])
            }}
            options={establishmentOptions || []}
            isDisabled={publicCompanyIsFetching || disabled}
            isLoading={createPublicCompanyLeadSpecialFeeIsPending}
          />

          {specialEntries?.map((specialEntry, index) => (
            <SpecialFeeItem
              key={specialEntry.id}
              specialEntry={specialEntry}
              setSpecialEntries={setSpecialEntries as Dispatch<SetStateAction<Partial<SpecialEntriesData>[]>>}
              index={index}
              createPublicCompanyLeadSpecialFee={createPublicCompanyLeadSpecialFee}
              createPublicCompanyLeadSpecialFeeIsPending={createPublicCompanyLeadSpecialFeeIsPending}
              deleteSpecialEntry={deleteSpecialEntry}
              disabled={disabled}
            />
          ))}
        </div>
      }
    />
  )
}
